import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import * as ambassadorWixDynamicPageRouterV1Page from './dynamic-page-router-v1-page-pages.http.js';

/** Stub entity, not used at the moment. */
export interface Page {
  /**
   * Page ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
}

export interface GetRouterSitemapCountRequest {
  /**
   * Router prefix that identifies the page type or origin. For TPA (Third-Party Application) pages, this contains the TPA page ID.
   * @maxLength 2048
   */
  routerPrefix?: string;
  /** Config containing request patterns, e.g. '/{title}' */
  config?: Config;
  /** Map of page roles containing page ID and title of each role */
  pageRoles?: Record<string, PageRole>;
  /**
   * Optional page name to filter patterns by matching page role title
   * @maxLength 200
   */
  pageName?: string;
  /**
   * Query param mapped to body, editor/site
   * @maxLength 200
   */
  viewMode?: string;
  /**
   * Query param mapped to body, unique for Wix code app
   * @format GUID
   */
  gridAppId?: string | null;
}

export interface Config {
  /** Map of URL patterns to their configurations. The key is the URL pattern (e.g., `/{title}`) and the value contains the pattern configuration. */
  patterns?: Record<string, Pattern>;
  /** Maps rule ID → PageRule (filter + variant page role). */
  rules?: Record<string, PageRule>;
  /** Maps original page role ID → ordered rule IDs. First matching rule wins. */
  roleVariations?: Record<string, any[]>;
}

export interface Pattern {
  /**
   * Page role identifier that links this pattern to a specific page template.
   * @maxLength 200
   */
  pageRole?: string;
  /**
   * Title pattern with field interpolation (e.g., "{title}, {author} - {category}").
   * Increased to 50000 to support complex title templates with many interpolated fields and long text content.
   * @maxLength 50000
   */
  title?: string;
  /** Configuration specifying which collection to query and any filters to apply. */
  config?: PatternConfig;
  /** SEO meta tags to be included in the page head. Keys are meta tag names, values are the content. */
  seoMetaTags?: Record<string, string>;
}

export interface PatternConfig {
  /**
   * Name of the Wix Data collection to query for dynamic content.
   * @maxLength 200
   */
  collection?: string;
  /** Filter criteria to apply when querying the collection. Uses MongoDB-style query syntax to limit which items generate routes. */
  filter?: Record<string, any> | null;
  /** Indicates if URLs in sitemap should be turned to lowercase. Defaults to true if not set. */
  lowercase?: boolean | null;
  /**
   * The field id that gets added to DataCollection and DataItems.
   * Indicates that page links is a slug based and items should be retrieved by slug fields present in the pattern.
   * @maxLength 1000
   */
  pageLinkId?: string | null;
  /**
   * Sort specification for query results. Each struct contains field name and direction (asc/desc).
   * @maxSize 32
   */
  sort?: Record<string, any>[] | null;
  /** Page size for pagination. */
  pageSize?: number | null;
  /** SEO v2 flag. */
  seoV2?: boolean | null;
  /** Cursor-based pagination flag. */
  cursor?: boolean | null;
  /**
   * Dataset includes array for passthrough config.
   * @maxSize 100
   * @maxLength 1000
   */
  includes?: string[];
  /**
   * Field groups to include in query results. Currently used only in App Collections.
   * Please refer to app collection documentation or collection field definitions for a list of valid values.
   * @maxSize 100
   * @maxLength 100
   */
  includeFieldGroups?: string[];
}

/** A variant rule: if filter matches the fetched data item, use page_role instead of the original. */
export interface PageRule {
  /**
   * Variant page role ID to return when this rule matches.
   * @maxLength 200
   */
  pageRole?: string;
  /** Wix Data filter evaluated against the fetched item. */
  filter?: Record<string, any> | null;
  /**
   * Human-readable rule name (for debugging/logging).
   * @maxLength 200
   */
  name?: string | null;
}

export interface PageRole {
  /**
   * Page role ID that uniquely identifies the page template.
   * @maxLength 200
   */
  _id?: string;
  /**
   * Human-readable title for the page role.
   * @maxLength 200
   */
  title?: string;
}

export interface GetRouterSitemapCountResponse {
  /** List of count results grouped by page name. */
  result?: RouterCountItem[];
}

export interface RouterCountItem {
  /**
   * Name of the dynamic page template.
   * @maxLength 200
   */
  pageName?: string;
  /** Number of inner routes for the given page. Capped at 1,000 for performance. */
  count?: number;
}

export interface GetRouterSitemapRequest {
  /**
   * Full URL to the inner route
   * @maxLength 2048
   */
  fullUrl?: string;
  /**
   * Router prefix (TPA page ID for TPA pages)
   * @maxLength 2048
   */
  routerPrefix?: string;
  /**
   * Optional page name to filter patterns by matching page role title
   * @maxLength 2048
   */
  pageName?: string;
  /** Config containing request patterns, e.g. '/{title}' */
  config?: Config;
  /** Map of page roles containing page ID and title of each role */
  pageRoles?: Record<string, PageRole>;
  /**
   * Query param mapped to body, editor/site
   * @maxLength 200
   */
  viewMode?: string;
  /**
   * Query param mapped to body, unique for Wix code app
   * @format GUID
   */
  gridAppId?: string | null;
}

export interface GetRouterSitemapResponse {
  /** List of sitemap entries containing URLs and metadata for dynamic pages. */
  result?: RouterRouteItem[];
}

export interface RouterRouteItem {
  /**
   * URL to inner route relative to current domain
   * @maxLength 500
   */
  url?: string;
  /**
   * Dynamic page name
   * @maxLength 200
   */
  pageName?: string;
  /**
   * Title of the specific route, typically derived from collection data.
   * @maxLength 200
   */
  title?: string;
  /**
   * Change frequency for sitemap SEO purposes (e.g., `daily`, `weekly`, `monthly`).
   * @maxLength 100
   */
  changeFrequency?: string;
  /**
   * Last modified timestamp in ISO 8601 format.
   * @maxLength 100
   */
  lastModified?: string | null;
  /** Priority for sitemap SEO purposes (0.0 to 1.0). */
  priority?: number;
}

export interface GetRouterPagesRequest {
  /**
   * Complete URL to the inner route. Used for context-aware processing and as a base for relative URL resolution.
   * Increased to 8192 to accommodate URLs with extensive query parameters (tracking, analytics, UTM params, etc.)
   * @maxLength 8192
   */
  fullUrl?: string;
  /**
   * Router prefix (TPA page ID for TPA pages)
   * @maxLength 2048
   */
  routerPrefix?: string;
  /**
   * Inner route suffix containing the dynamic part of the URL pattern. For example, `/{title}` or `/category/{category}/post/{slug}`.
   * Increased to 8192 to match full_url capacity for complex dynamic routes
   * @maxLength 8192
   */
  routerSuffix?: string;
  /** Additional request information including form factor (desktop/mobile) for device-appropriate routing. */
  requestInfo?: RequestInfo;
  /** Config containing request patterns, e.g. '/{title}' */
  config?: Config;
  /** Map of page roles containing page ID and title of each role */
  pageRoles?: Record<string, PageRole>;
  /**
   * Query param mapped to body, editor/site
   * @maxLength 200
   */
  viewMode?: string;
  /**
   * Query param mapped to body, unique for Wix code app
   * @format GUID
   */
  gridAppId?: string | null;
}

export interface RequestInfo {
  /**
   * Device form factor, typically `desktop` or `mobile`. Used to serve device-appropriate page layouts.
   * @maxLength 200
   */
  formFactor?: string;
}

export interface GetRouterPagesResponse {
  /** Page resolution result containing status, data, and metadata. */
  result?: RouterPagesResult;
}

export interface RouterPagesResult {
  /** HTTP status code of the page (200, 302, 404) */
  status?: number;
  /**
   * Name of the dynamic page template that should be used for rendering.
   * @maxLength 200
   */
  page?: string;
  /** Application router data available only to internal Wix apps. Contains system-level and administrative information. */
  data?: Record<string, any> | null;
  /** SEO and metadata information for the page head section. */
  head?: RouterPageHead;
  /**
   * Error message when status indicates an error condition.
   * @maxLength 500
   */
  message?: string;
  /**
   * Target URL for redirects when status is 302.
   * @maxLength 2048
   */
  redirectUrl?: string;
}

export interface RouterPageHead {
  /**
   * Page title for SEO and browser display.
   * @maxLength 200
   */
  title?: string;
  /** Meta tags for SEO optimization. Keys are meta tag names, values are the content. */
  metaTags?: Record<string, string>;
  /**
   * Page description for SEO. Extracted from seoMetaTags if present.
   * @maxLength 5000
   */
  description?: string;
  /**
   * Keywords for SEO. Extracted from seoMetaTags if present.
   * @maxLength 5000
   */
  keywords?: string;
  /**
   * NoIndex directive for search engines. Extracted from seoMetaTags if present.
   * @maxLength 500
   */
  noIndex?: string;
}

/** Viewer GET request: .r parameter containing {urlParams, body} envelope. */
export interface GetRouterPagesCachedRequest {
  /** URL parameters from the viewer's query string. */
  urlParams?: GetRouterPagesCachedUrlParams;
  /** Request body from the viewer's envelope. */
  body?: GetRouterPagesRequest;
}

export interface GetRouterPagesCachedUrlParams {
  /**
   * Unique for Wix code app
   * @format GUID
   */
  gridAppId?: string | null;
  /**
   * Editor/site
   * @maxLength 200
   */
  viewMode?: string;
}

export interface GetDraftPreviewTokenRequest {
  /**
   * Wix Data collection ID to get a draft preview key for.
   * @maxLength 256
   */
  collectionId?: string;
}

export interface GetDraftPreviewTokenResponse {
  /**
   * Draft preview token.
   * @maxLength 10000
   */
  token?: string;
}

/**
 * Returns the count of sitemap entries grouped by page name.
 *
 * Use this endpoint for analytics dashboards, capacity planning, and understanding
 * the scale of dynamic content without retrieving actual URLs. Results are capped
 * at 1,000 items per pattern for performance.
 * @public
 * @documentationMaturity preview
 * @permissionId dynamic_page_router:v1:page:get_router_sitemap_count
 * @applicableIdentity APP
 * @fqn wix.dynamic_page_router.v1.DynamicPageRouter.GetRouterSitemapCount
 */
export async function getRouterSitemapCount(
  options?: GetRouterSitemapCountOptions
): Promise<
  NonNullablePaths<
    GetRouterSitemapCountResponse,
    `result` | `result.${number}.pageName` | `result.${number}.count`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    routerPrefix: options?.routerPrefix,
    config: options?.config,
    pageRoles: options?.pageRoles,
    pageName: options?.pageName,
    viewMode: options?.viewMode,
    gridAppId: options?.gridAppId,
  });

  const reqOpts =
    ambassadorWixDynamicPageRouterV1Page.getRouterSitemapCount(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          routerPrefix: '$[0].routerPrefix',
          config: '$[0].config',
          pageRoles: '$[0].pageRoles',
          pageName: '$[0].pageName',
          viewMode: '$[0].viewMode',
          gridAppId: '$[0].gridAppId',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface GetRouterSitemapCountOptions {
  /**
   * Router prefix that identifies the page type or origin. For TPA (Third-Party Application) pages, this contains the TPA page ID.
   * @maxLength 2048
   */
  routerPrefix?: string;
  /** Config containing request patterns, e.g. '/{title}' */
  config?: Config;
  /** Map of page roles containing page ID and title of each role */
  pageRoles?: Record<string, PageRole>;
  /**
   * Optional page name to filter patterns by matching page role title
   * @maxLength 200
   */
  pageName?: string;
  /**
   * Query param mapped to body, editor/site
   * @maxLength 200
   */
  viewMode?: string;
  /**
   * Query param mapped to body, unique for Wix code app
   * @format GUID
   */
  gridAppId?: string | null;
}

/**
 * Returns sitemap entries containing URLs, page names, and page titles.
 *
 * Use this endpoint to generate XML sitemaps for search engines, build navigation
 * menus, or provide URL listings to external systems. The `fullUrl` parameter
 * provides context for scoping sitemap generation to specific domains or URL hierarchies.
 * @public
 * @documentationMaturity preview
 * @permissionId dynamic_page_router:v1:page:get_router_sitemap
 * @applicableIdentity APP
 * @fqn wix.dynamic_page_router.v1.DynamicPageRouter.GetRouterSitemap
 */
export async function getRouterSitemap(
  options?: GetRouterSitemapOptions
): Promise<
  NonNullablePaths<
    GetRouterSitemapResponse,
    | `result`
    | `result.${number}.url`
    | `result.${number}.pageName`
    | `result.${number}.title`
    | `result.${number}.changeFrequency`
    | `result.${number}.priority`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    fullUrl: options?.fullUrl,
    routerPrefix: options?.routerPrefix,
    pageName: options?.pageName,
    config: options?.config,
    pageRoles: options?.pageRoles,
    viewMode: options?.viewMode,
    gridAppId: options?.gridAppId,
  });

  const reqOpts =
    ambassadorWixDynamicPageRouterV1Page.getRouterSitemap(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          fullUrl: '$[0].fullUrl',
          routerPrefix: '$[0].routerPrefix',
          pageName: '$[0].pageName',
          config: '$[0].config',
          pageRoles: '$[0].pageRoles',
          viewMode: '$[0].viewMode',
          gridAppId: '$[0].gridAppId',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface GetRouterSitemapOptions {
  /**
   * Full URL to the inner route
   * @maxLength 2048
   */
  fullUrl?: string;
  /**
   * Router prefix (TPA page ID for TPA pages)
   * @maxLength 2048
   */
  routerPrefix?: string;
  /**
   * Optional page name to filter patterns by matching page role title
   * @maxLength 2048
   */
  pageName?: string;
  /** Config containing request patterns, e.g. '/{title}' */
  config?: Config;
  /** Map of page roles containing page ID and title of each role */
  pageRoles?: Record<string, PageRole>;
  /**
   * Query param mapped to body, editor/site
   * @maxLength 200
   */
  viewMode?: string;
  /**
   * Query param mapped to body, unique for Wix code app
   * @format GUID
   */
  gridAppId?: string | null;
}

/**
 * Returns comprehensive page information for a specific route.
 * Called by the editor via POST with flat body + query params.
 * @public
 * @documentationMaturity preview
 * @permissionId dynamic_page_router:v1:page:get_router_pages
 * @applicableIdentity APP
 * @fqn wix.dynamic_page_router.v1.DynamicPageRouter.GetRouterPages
 */
export async function getRouterPages(
  options?: GetRouterPagesOptions
): Promise<
  NonNullablePaths<
    GetRouterPagesResponse,
    | `result.status`
    | `result.page`
    | `result.head.title`
    | `result.head.description`
    | `result.head.keywords`
    | `result.head.noIndex`
    | `result.message`
    | `result.redirectUrl`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    fullUrl: options?.fullUrl,
    routerPrefix: options?.routerPrefix,
    routerSuffix: options?.routerSuffix,
    requestInfo: options?.requestInfo,
    config: options?.config,
    pageRoles: options?.pageRoles,
    viewMode: options?.viewMode,
    gridAppId: options?.gridAppId,
  });

  const reqOpts = ambassadorWixDynamicPageRouterV1Page.getRouterPages(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          fullUrl: '$[0].fullUrl',
          routerPrefix: '$[0].routerPrefix',
          routerSuffix: '$[0].routerSuffix',
          requestInfo: '$[0].requestInfo',
          config: '$[0].config',
          pageRoles: '$[0].pageRoles',
          viewMode: '$[0].viewMode',
          gridAppId: '$[0].gridAppId',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface GetRouterPagesOptions {
  /**
   * Complete URL to the inner route. Used for context-aware processing and as a base for relative URL resolution.
   * Increased to 8192 to accommodate URLs with extensive query parameters (tracking, analytics, UTM params, etc.)
   * @maxLength 8192
   */
  fullUrl?: string;
  /**
   * Router prefix (TPA page ID for TPA pages)
   * @maxLength 2048
   */
  routerPrefix?: string;
  /**
   * Inner route suffix containing the dynamic part of the URL pattern. For example, `/{title}` or `/category/{category}/post/{slug}`.
   * Increased to 8192 to match full_url capacity for complex dynamic routes
   * @maxLength 8192
   */
  routerSuffix?: string;
  /** Additional request information including form factor (desktop/mobile) for device-appropriate routing. */
  requestInfo?: RequestInfo;
  /** Config containing request patterns, e.g. '/{title}' */
  config?: Config;
  /** Map of page roles containing page ID and title of each role */
  pageRoles?: Record<string, PageRole>;
  /**
   * Query param mapped to body, editor/site
   * @maxLength 200
   */
  viewMode?: string;
  /**
   * Query param mapped to body, unique for Wix code app
   * @format GUID
   */
  gridAppId?: string | null;
}

/**
 * Returns comprehensive page information for a specific route (cacheable).
 * Called by the viewer via GET with .r parameter containing {urlParams, body} envelope.
 * @public
 * @documentationMaturity preview
 * @permissionId dynamic_page_router:v1:page:get_router_pages
 * @applicableIdentity APP
 * @fqn wix.dynamic_page_router.v1.DynamicPageRouter.GetRouterPagesCached
 */
export async function getRouterPagesCached(
  options?: GetRouterPagesCachedOptions
): Promise<
  NonNullablePaths<
    GetRouterPagesResponse,
    | `result.status`
    | `result.page`
    | `result.head.title`
    | `result.head.description`
    | `result.head.keywords`
    | `result.head.noIndex`
    | `result.message`
    | `result.redirectUrl`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    urlParams: options?.urlParams,
    body: options?.body,
  });

  const reqOpts =
    ambassadorWixDynamicPageRouterV1Page.getRouterPagesCached(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          urlParams: '$[0].urlParams',
          body: '$[0].body',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface GetRouterPagesCachedOptions {
  /** URL parameters from the viewer's query string. */
  urlParams?: GetRouterPagesCachedUrlParams;
  /** Request body from the viewer's envelope. */
  body?: GetRouterPagesRequest;
}

/**
 * Returns the preview token used for draft collection previews.
 * @public
 * @documentationMaturity preview
 * @permissionId dynamic_page_router:v1:page:get_draft_preview_token
 * @applicableIdentity APP
 * @fqn wix.dynamic_page_router.v1.DynamicPageRouter.GetDraftPreviewToken
 */
export async function getDraftPreviewToken(
  options?: GetDraftPreviewTokenOptions
): Promise<NonNullablePaths<GetDraftPreviewTokenResponse, `token`, 2>> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    collectionId: options?.collectionId,
  });

  const reqOpts =
    ambassadorWixDynamicPageRouterV1Page.getDraftPreviewToken(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { collectionId: '$[0].collectionId' },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface GetDraftPreviewTokenOptions {
  /**
   * Wix Data collection ID to get a draft preview key for.
   * @maxLength 256
   */
  collectionId?: string;
}
